class LunioraShopMenu extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.tabs = Array.from(this.querySelectorAll('[data-shop-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-shop-panel]'));
    this.carousels = Array.from(this.querySelectorAll('[data-shop-carousel]'));

    this.setupMobileDrawerToggle();

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activate(index, true));
      tab.addEventListener('focus', () => {
        if (matchMedia('(hover: hover) and (pointer: fine)').matches) this.activate(index, false);
      });
      tab.addEventListener('pointerenter', () => {
        if (matchMedia('(hover: hover) and (pointer: fine)').matches) this.activate(index, false);
      });
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.carousels.forEach((carousel) => this.setupCarousel(carousel));
  }

  setupMobileDrawerToggle() {
    if (!this.classList.contains('luniora-shop-menu--mobile')) return;

    const details = this.closest('details.menu-drawer-container');
    const toggle = details?.querySelector('[data-luniora-menu-toggle]');
    const drawerComponent = details?.closest('header-drawer');
    if (!details || !toggle || toggle.dataset.lunioraToggleBound === 'true') return;

    toggle.dataset.lunioraToggleBound = 'true';
    toggle.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const isOpen = toggle.getAttribute('aria-expanded') === 'true' || details.classList.contains('menu-open');

        if (isOpen) {
          if (typeof drawerComponent?.close === 'function') {
            drawerComponent.close();
          } else {
            toggle.setAttribute('aria-expanded', 'false');
            details.classList.remove('menu-open');
            window.setTimeout(() => {
              details.open = false;
            }, 350);
          }
          return;
        }

        details.open = true;
        toggle.setAttribute('aria-expanded', 'true');

        if (typeof drawerComponent?.open === 'function') {
          drawerComponent.open();
        } else {
          requestAnimationFrame(() => details.classList.add('menu-open'));
        }
      },
      true
    );
  }

  activate(index, moveFocus = false) {
    if (!this.tabs[index] || !this.panels[index]) return;

    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    requestAnimationFrame(() => {
      this.carousels.forEach((carousel) => this.updateCarousel(carousel));
    });

    if (moveFocus) this.tabs[index].focus({ preventScroll: true });
  }

  setupCarousel(carousel) {
    const track = carousel.querySelector('[data-shop-carousel-track]');
    const previous = carousel.querySelector('[data-shop-carousel-prev]');
    const next = carousel.querySelector('[data-shop-carousel-next]');
    if (!track || !previous || !next) return;

    previous.addEventListener('click', () => this.moveCarousel(track, -1));
    next.addEventListener('click', () => this.moveCarousel(track, 1));
    track.addEventListener('scroll', () => this.updateCarousel(carousel), { passive: true });

    const observer = new ResizeObserver(() => this.updateCarousel(carousel));
    observer.observe(track);
    requestAnimationFrame(() => this.updateCarousel(carousel));
  }

  moveCarousel(track, direction) {
    const distance = Math.max(track.clientWidth * 0.82, 220);
    track.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }

  updateCarousel(carousel) {
    const track = carousel.querySelector('[data-shop-carousel-track]');
    const previous = carousel.querySelector('[data-shop-carousel-prev]');
    const next = carousel.querySelector('[data-shop-carousel-next]');
    if (!track || !previous || !next || track.clientWidth === 0) return;

    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = maximum > 2;
    previous.hidden = !hasOverflow;
    next.hidden = !hasOverflow;
    previous.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= maximum - 2;
  }

  onKeydown(event, index) {
    let nextIndex = index;

    if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === index) return;

    event.preventDefault();
    this.activate(nextIndex, true);
  }
}

if (!customElements.get('luniora-shop-menu')) {
  customElements.define('luniora-shop-menu', LunioraShopMenu);
}
